import { Vector2 } from "three";
import { isDefined } from "../../infra3d";
import { STREAMTYPE } from "../measure/internal";
// import { StereoStream, CubeMapStream } from "../ennetwork/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
/**
 * Tool used to create/delete FrameFeatures
 * @class FrameFeatureTool
 * @extends Tool
 * @constructor
 */
export class FrameFeatureTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        /**
         * keycodes for arrow keys
         * @enum {number}
         * @typedef {number} KeyCodeEnum
         */
        this.keyCodeEnum = {
            ARROW_LEFT: 37,
            ARROW_UP: 38,
            ARROW_RIGHT: 39,
            ARROW_DOWN: 40,
        };
        this.framefeaturecollection = null;
        this.framefeature = null;
        this.style = null;
        this.toolstate = 0;
        this.stateEnum = {
            DRAW: 0,
            SELECT: 1,
            DRAG: 2,
        };
        /**
         * coords of the first click
         * @property {Vector2} firstcoords
         * @default null
         */
        this._firstcoords = [];
        /**
         * last coords before leaving callote
         * @type {Array}
         */
        this._lastvalidcoords = [];
        /**
         * all selected/highlighted features
         * @type {Array}
         * @private
         */
        this._selectedFeatures = [];
        /**
         * datatype to save the point of first access to a calotte
         * to calculate deltaX and deltaY later; idx eq. calloteId
         *
         * @type {{entered: boolean, x: number, y: number}[]}
         * @private
         */
        this._calloteEntered = [
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
        ];
        /**
         * true if top callote is only active callote
         * @type {boolean}
         * @private
         */
        this._aborted = false;
        /**
         * last single active callote
         * @type {number}
         * @private
         */
        this._callotepick = 0;
        // event variables
        this.mousedowneventnr = null;
        this.mouseupeventnr = null;
        this.mousemoveeventnr = null;
        this.resetinputevent = null;
        this.geoframechangeeventnr = null;
        this.finishinputevent = null;
        this.resetinputtevent = null;
        this.holdeventnr = null;
        this.releaseeventnr = null;
        this.drageventnr = null;
        this.deleteeventnr = null;
        this.leftarrowevt = null;
        this.rightarrowevt = null;
        this.uparrowevt = null;
        this.downarrowevt = null;
        this.p0 = null;
        /**
         * Data-Structure for CubeFaces, to easily access cubefaces by sensor-id
         * @type {{0: any; 1: any; 2: any; 3: any; 4: any; 5: any; numfeatures: number}}
         */
        this.featureByCubeFace = {
            0: null,
            1: null,
            2: null,
            3: null,
            4: null,
            5: null,
            numfeatures: 0,
        };
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const defaultoptions = {
            finishedcbf: null,
            selectedcbf: null,
            errorcbf: null,
            enablecbf: null,
            disablecbf: null,
            finishinput: USERINPUTTYPE.LEFTCLICK,
            resetinput: USERINPUTTYPE.RIGHTCLICK,
            resetcbf: null,
            activecursor: "crosshair",
            continousdraw: false,
            continousdrawcbf: null,
            maxdist: 20,
            modifycursor: true,
            deletecbf: null,
            singlepano: false,
            style: null,
            defaultprops: null,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        this.framefeaturecollection = this.scene
            .getFrameFeatureHandler()
            .createFeatureCollection("mmFrameFeatureTool_internal");
        if (this.options.style)
            this.framefeaturecollection.changeStyle(this.options.style);
        if (this.options.defaultprops)
            this.framefeaturecollection.setDefaultProps(this.options.defaultprops);
        this.type = "FrameFeatureTool";
    }
    /**
     * internal function - to activate tool call .enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        if (this.enabled)
            return;
        this._subscribeEvents();
        this.enabled = true;
        this.fire(FrameFeatureTool.enabled, this.options["cbobject"]);
    }
    /**
     * internal function - to disable tool call .disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        if (!this.enabled)
            return; //if tool is not active there is nothing to disable...
        this.reset();
        this.enabled = false;
        this.scene.getCursorStyleHandler().setCursorStyle("default");
        this.fire(FrameFeatureTool.disabled, this.options["cbobject"]);
        this._unSubscribeEvents();
    }
    _subscribeEvents() {
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        }
        this.geoframechangeeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this._onFrameChanged, this);
        this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        this.mousedowneventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEDOWN, this._onMouseDown, this);
        this.mouseupeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEUP, this._onMouseUp, this);
        this.deleteeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_DELETE_UP, this._onKeyDelete, this);
        this.leftarrowevt = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_ARROW_LEFT, this._onArrow, this);
        this.rightarrowevt = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_ARROW_RIGHT, this._onArrow, this);
        this.uparrowevt = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_ARROW_UP, this._onArrow, this);
        this.downarrowevt = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_ARROW_DOWN, this._onArrow, this);
        if (this.options.resetinput != null) {
            this.resetinputtevent = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this.reset, this);
        }
        if (this.options.enablecbf != null) {
            this.on(FrameFeatureTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(FrameFeatureTool.disabled, this.options.disablecbf);
        }
        if (this.options.deletecbf != null) {
            this.on(FrameFeatureTool.deleted, this.options.deletecbf);
        }
        if (this.options.selectedcbf != null) {
            this.on(FrameFeatureTool.selected, this.options.selectedcbf);
        }
        if (this.options.resetcbf != null) {
            this.on(FrameFeatureTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.on(FrameFeatureTool.finished, this.options.finishedcbf);
        }
    }
    _unSubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.GEOFRAMECHANGED, this.geoframechangeeventnr);
        this.geoframechangeeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEDOWN, this.mousedowneventnr);
        this.mousedowneventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEUP, this.mouseupeventnr);
        this.mouseupeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_DELETE_UP, this.deleteeventnr);
        this.deleteeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_ARROW_LEFT, this.leftarrowevt);
        this.leftarrowevt = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_ARROW_RIGHT, this.rightarrowevt);
        this.rightarrowevt = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_ARROW_UP, this.uparrowevt);
        this.uparrowevt = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_ARROW_DOWN, this.downarrowevt);
        this.downarrowevt = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.finishinput, this.finishinputevent);
        this.finishinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.resetinput, this.resetinputtevent);
        this.resetinputtevent = null;
        if (this.options.enablecbf != null) {
            this.off(FrameFeatureTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(FrameFeatureTool.disabled, this.options.disablecbf);
        }
        if (this.options.deletecbf != null) {
            this.off(FrameFeatureTool.deleted, this.options.deletecbf);
        }
        if (this.options.selectedcbf != null) {
            this.off(FrameFeatureTool.selected, this.options.selectedcbf);
        }
        if (this.options.resetcbf != null) {
            this.off(FrameFeatureTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.off(FrameFeatureTool.finished, this.options.finishedcbf);
        }
    }
    _onFrameChanged(evt) {
        for (const idx in this._selectedFeatures) {
            this._selectedFeatures[idx].highlightOff();
        }
        this._selectedFeatures = [];
    }
    _onKeyDelete(evt) {
        this.fire(FrameFeatureTool.deleted, this.options["cbobject"], this._selectedFeatures);
        this._selectedFeatures = [];
    }
    /**
     * Change size and position of the currently selcted framefeatures;
     * Arrowkeys -> reposition
     * Shift + Arrowkeys -> resize
     * @param evt
     * @private
     * @method _onArrow
     */
    _onArrow(evt) {
        for (let i = 0; i < this._selectedFeatures.length; i++) {
            let mod = this._selectedFeatures[i].h / 10;
            if (this._selectedFeatures[i].w < this._selectedFeatures[i].h) {
                mod = this._selectedFeatures[i].w / 10;
            }
            switch (evt.keyCode) {
                case this.keyCodeEnum.ARROW_DOWN:
                    //make box smaller and recenter
                    if (evt.shiftKey) {
                        this._selectedFeatures[i].ny += mod / 2;
                        this._selectedFeatures[i].h -= mod;
                    }
                    else {
                        //move box down
                        this._selectedFeatures[i].ny += mod;
                    }
                    break;
                case this.keyCodeEnum.ARROW_UP:
                    //make box taller and recenter
                    if (evt.shiftKey) {
                        this._selectedFeatures[i].ny -= mod / 2;
                        this._selectedFeatures[i].h += mod;
                    }
                    else {
                        //move box up
                        this._selectedFeatures[i].ny -= mod;
                    }
                    break;
                case this.keyCodeEnum.ARROW_LEFT:
                    //make box narrower and recenter
                    if (evt.shiftKey) {
                        this._selectedFeatures[i].nx += mod / 2;
                        this._selectedFeatures[i].w -= mod;
                    }
                    else {
                        //move box left
                        this._selectedFeatures[i].nx -= mod;
                    }
                    break;
                case this.keyCodeEnum.ARROW_RIGHT:
                    //make box wider and recenter
                    if (evt.shiftKey) {
                        this._selectedFeatures[i].nx -= mod / 2;
                        this._selectedFeatures[i].w += mod;
                    }
                    else {
                        //move box right
                        this._selectedFeatures[i].nx += mod;
                    }
                    break;
            }
            this._selectedFeatures[i].properties.h = this._selectedFeatures[i].h;
            this._selectedFeatures[i].properties.w = this._selectedFeatures[i].w;
            this._selectedFeatures[i].properties.nx = this._selectedFeatures[i].nx;
            this._selectedFeatures[i].properties.ny = this._selectedFeatures[i].ny;
            this._selectedFeatures[i].frameFeatureBox.resize(this._selectedFeatures[i].properties.nx, this._selectedFeatures[i].properties.ny, this._selectedFeatures[i].properties.w, this._selectedFeatures[i].properties.h);
        }
        this.scene.infrascene.render();
    }
    /**
     * start FrameFeature or select already existing
     * @param evt
     * @private
     */
    _onMouseDown(evt) {
        if (evt.button == 1) {
            return;
        }
        else if (evt.button == 2 && this.options.resetinput == USERINPUTTYPE.RIGHTCLICK) {
            this._aborted = true;
            return;
        }
        const picked = this.scene.getFeatureHandler().pick(evt.offsetX, evt.offsetY);
        if (!picked) {
            this.toolstate = this.stateEnum.DRAW;
            this.scene.infrascene.traversalState.lockNavigation = true;
        }
        else {
            this.toolstate = this.stateEnum.SELECT;
            this.scene.infrascene.traversalState.lockNavigation = false;
        }
        switch (this.toolstate) {
            case this.stateEnum.DRAW: {
                this._selectedFeatures.forEach(function (ff) {
                    this.selectFrameFeature(ff);
                }, this);
                const mi = this.scene.getCurrentGeoframe().getMeasureInfo();
                const mh = this.scene.getMeasureHandler();
                const mapCoords = mh.mouseToMap(evt.offsetX, evt.offsetY, this.scene.getCurrentGeoframe(), true);
                if (mi.streamtype == STREAMTYPE.CALOTTE) {
                    for (let i = 0; i < mapCoords.length; i++) {
                        this._firstcoords.push(mapCoords[i].coords);
                        this._lastvalidcoords.push(this._firstcoords[i]);
                        this._startFeature(this._firstcoords[i], mapCoords[i].sensorid, mapCoords[i].distance);
                        this._calloteEntered[mapCoords[i].sensorid].x = this._firstcoords[i].x;
                        this._calloteEntered[mapCoords[i].sensorid].y = this._firstcoords[i].y;
                        this._calloteEntered[mapCoords[i].sensorid].entered = true;
                    }
                }
                else if (mi.streamtype == STREAMTYPE.CUBEMAP) {
                    this.p0 = mapCoords[0];
                    this.addFeatureByCubeFace(this.p0);
                }
                else {
                    let sensorid = 0;
                    const geoframecollection = this.scene.getCurrentGeoFrameCollection();
                    if (mi.streamtype == STREAMTYPE.STEREO)
                        sensorid = geoframecollection.activeimage == "left" ? 0 : 1;
                    this._firstcoords.push(mapCoords[0].coords);
                    this._startFeature(this._firstcoords[0], sensorid);
                }
                this.toolstate = this.stateEnum.DRAG;
                break;
            }
            case this.stateEnum.SELECT: {
                this.selectFrameFeature(picked[0].feature);
                break;
            }
        }
    }
    /**
     * finish FrameFeature and cleanUp
     * @private
     */
    _onMouseUp(evt) {
        if (this.toolstate === this.stateEnum.DRAG) {
            if (this._aborted) {
                this._aborted = false;
            }
            else {
                const mi = this.scene.getCurrentGeoframe().getMeasureInfo();
                if (mi.streamtype == STREAMTYPE.CALOTTE || mi.streamtype == STREAMTYPE.CUBEMAP) {
                    if (this.framefeaturecollection.frameFeatures[0].w > 0.001 &&
                        this.framefeaturecollection.frameFeatures[0].h > 0.001) {
                        this.fire(FrameFeatureTool.finished, this.options["cbobject"], {
                            ffc: this.framefeaturecollection,
                        }, evt);
                    }
                }
                else {
                    if (this.framefeature.w > 0.001 && this.framefeature.h > 0.001) {
                        this.fire(FrameFeatureTool.finished, this.options["cbobject"], {
                            ffc: this.framefeaturecollection,
                        }, evt);
                    }
                }
                this.scene.infrascene.traversalState.resetUserEvents();
            }
            this.toolstate = this.stateEnum.SELECT;
        }
        this._cleanUp();
    }
    /**
     * Adds a FrameFeatures to the FrameFeaturecollection and stores a pointer in the featureByCubeFace DataStructure
     * It's a wrapper for CubeFace Handling in FeatureCollection
     * @param mouseToMapInfo
     */
    addFeatureByCubeFace(mouseToMapInfo) {
        this.featureByCubeFace[mouseToMapInfo.cubefaceindex] = this._startFeature(mouseToMapInfo.coords, mouseToMapInfo.cubefaceindex, mouseToMapInfo.distance);
        this.featureByCubeFace.numfeatures += 1;
    }
    /**
     * Removes the FrameFeature in Featurecollection and in the featureByCubeFace DataStructure as well
     * @param cubeFaceId
     */
    removeFeatureByCubeFace(cubeFaceId) {
        cubeFaceId.forEach((id) => {
            const f = this.featureByCubeFace[id];
            if (f != null) {
                this.framefeaturecollection.destroyFrameFeature(f);
                this.featureByCubeFace[id] = null;
                this.featureByCubeFace.numfeatures -= 1;
            }
        });
    }
    /**
     * resizing and creating of FrameFeatures while moving
     * mouse and holding left mousebutton; see comments in
     * code for further information
     *
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.toolstate === this.stateEnum.DRAG) {
            if (!this._aborted) {
                const mi = this.scene.getCurrentGeoframe().getMeasureInfo();
                const mh = this.scene.getMeasureHandler();
                const mapInfos = mh.mouseToMap(evt.offsetX, evt.offsetY, this.scene.getCurrentGeoframe(), true);
                if (mi.streamtype == STREAMTYPE.CUBEMAP) {
                    /**
                     * p0 - is starting point of framefeature drawing
                     * p2 - is point on mousepointer
                     *
                     *   p0 ------
                     *      |     |
                     *      |     |
                     *      ------- p2
                     */
                    const p0 = this.p0;
                    const p2 = mapInfos[0];
                    //todo: cast to cubegeoframecollection once its implemented
                    const qt = (this.scene.getCurrentGeoFrameCollection().imagequadtree);
                    //get clamped map coordinates for p0 - we need to go through 3d space, because p0 and p2 could be on different cube-faces
                    const p0_on_start_face = this.p0.coords;
                    const p2_3d = qt.mapTo3D(p2.coords.x, p2.coords.y, p2.cubefaceindex);
                    const p2_on_start_face = qt.pos3dtoMap(p2_3d, p0.cubefaceindex); //clamped map-coordinate if p2 lies on another face
                    //get clamped map coordinates for p0 - we need to go through 3d space, because it p0 and p2 could be on different cube-faces
                    const p0_3d = qt.mapTo3D(p0.coords.x, p0.coords.y, p0.cubefaceindex);
                    const p0_on_current_face = qt.pos3dtoMap(p0_3d, p2.cubefaceindex);
                    const p2_on_current_face = p2.coords;
                    //add a feature if we're on a new face
                    if (this.featureByCubeFace[p2.cubefaceindex] == null)
                        this.addFeatureByCubeFace(p2);
                    //resize framefeatures - acoording clamped coordinates
                    this.featureByCubeFace[p0.cubefaceindex].resize(p2_on_start_face, p0_on_start_face);
                    this.featureByCubeFace[p2.cubefaceindex].resize(p2_on_current_face, p0_on_current_face);
                    //remove all framefeatures of faces wich are not involved
                    this.removeFeatureByCubeFace([0, 1, 2, 3, 4, 5].filter((e) => {
                        return e != p2.cubefaceindex && e != p0.cubefaceindex;
                    }));
                }
                else if (mi.streamtype == STREAMTYPE.CALOTTE) {
                    //resize all existing framefeatures
                    let movingLeft = null;
                    for (let j = 0; j < this.framefeaturecollection.frameFeatures.length; j++) {
                        let dropout = true;
                        const recentCalloteId = this.framefeaturecollection.frameFeatures[j].calloteId;
                        for (let i = 0; i < mapInfos.length; i++) {
                            movingLeft = this._calloteEntered[mapInfos[i].sensorid].x > mapInfos[i].coords.x;
                            if (recentCalloteId == mapInfos[i].sensorid) {
                                this.framefeaturecollection.frameFeatures[j].resize(mapInfos[i].coords, this._firstcoords[j]);
                                this._lastvalidcoords[j] = mapInfos[i].coords;
                                dropout = false;
                            }
                        }
                        //resize all features that are not in active callotes
                        if (dropout) {
                            let newX, newY;
                            let neighbourCallote;
                            if (mapInfos[mapInfos.length - 1].sensorid != 5) {
                                if (movingLeft) {
                                    neighbourCallote = this._calloteEntered[recentCalloteId == 0 ? 4 : recentCalloteId - 1];
                                    newX = 0;
                                    newY =
                                        this._calloteEntered[recentCalloteId].y -
                                            (neighbourCallote.y - mapInfos[0].coords.y);
                                }
                                else {
                                    neighbourCallote = this._calloteEntered[recentCalloteId == 4 ? 0 : recentCalloteId + 1];
                                    const max_map = mh.sensorToMap(new Vector2(mi.imagemeta.width, mi.imagemeta.height));
                                    newX = max_map.x;
                                    newY =
                                        this._calloteEntered[recentCalloteId].y -
                                            (neighbourCallote.y - mapInfos[0].coords.y);
                                }
                                const offmapcoords = new Vector2(newX, newY);
                                this.framefeaturecollection.frameFeatures[j].resize(offmapcoords, this._firstcoords[j]);
                            }
                        }
                    }
                    //abort marking if top callote is the only active callote
                    if (mapInfos.length == 1) {
                        if (mapInfos[0].sensorid == 5) {
                            this._aborted = true;
                            this._cleanUp();
                        }
                        else {
                            this._callotepick = mapInfos[0].sensorid;
                            for (let j = 0; j < this.framefeaturecollection.frameFeatures.length; j++) {
                                const currentPos = this.framefeaturecollection.frameFeatures[j].calloteId;
                                const currentX = this.framefeaturecollection.frameFeatures[j].nx;
                                const movingLeft = this._calloteEntered[this._callotepick].x > mapInfos[0].coords.x;
                                const max_map = mh.sensorToMap(new Vector2(mi.imagemeta.width, mi.imagemeta.height));
                                if (((movingLeft &&
                                    currentX + this.framefeaturecollection.frameFeatures[j].w >= max_map.x) ||
                                    (!movingLeft && currentX == 0)) &&
                                    currentPos != this._callotepick) {
                                    this._calloteEntered[currentPos].entered = false;
                                    this.framefeaturecollection.destroyFrameFeature(this.framefeaturecollection.frameFeatures[j]);
                                    this._lastvalidcoords[j] = null;
                                    this._firstcoords[j] = null;
                                    this._lastvalidcoords.splice(j, 1);
                                    this._firstcoords.splice(j, 1);
                                }
                            }
                        }
                    }
                    else if (mapInfos.length == 2) {
                        for (let i = 0; i < mapInfos.length; i++) {
                            if (mapInfos[i].sensorid != 5) {
                                let existing = false;
                                //check if already exists
                                let j;
                                for (j = 0; j < this.framefeaturecollection.frameFeatures.length; j++) {
                                    if (this.framefeaturecollection.frameFeatures[j].calloteId == mapInfos[i].sensorid) {
                                        existing = true;
                                    }
                                }
                                // create if not
                                if (!existing) {
                                    const lastIdx = this.framefeaturecollection.frameFeatures.length;
                                    //coming from the left or the right?
                                    const panoIdx = this._callotepick == mapInfos[0].sensorid ? 0 : 1;
                                    const max_map = mh.sensorToMap(new Vector2(mi.imagemeta.width, mi.imagemeta.height));
                                    this._firstcoords.push(new Vector2(mapInfos[panoIdx].coords.x > 0.5 ? 0 : max_map.x, this._firstcoords[0].y));
                                    this._lastvalidcoords.push(this._firstcoords[lastIdx]);
                                    this._startFeature(this._firstcoords[lastIdx], mapInfos[i].sensorid, mapInfos[i].distance);
                                    this._calloteEntered[mapInfos[i].sensorid].x = this._firstcoords[j].x;
                                    this._calloteEntered[mapInfos[i].sensorid].y = this._firstcoords[j].y;
                                    this._calloteEntered[mapInfos[i].sensorid].entered = true;
                                }
                            }
                        }
                    }
                    //only show framefeature thats closer to the center of a callote
                    if (this.options.singlepano) {
                        // sort the frame-features by their distance to the callote. The one with the shortest
                        // distance ist the visible one.
                        this.framefeaturecollection.frameFeatures.sort((a, b) => {
                            return a.properties.distance - b.properties.distance;
                        });
                        for (let i = 0; i < this.framefeaturecollection.frameFeatures.length; i++) {
                            if (i == 0) {
                                this.framefeaturecollection.frameFeatures[i].show();
                            }
                            else {
                                this.framefeaturecollection.frameFeatures[i].hide();
                            }
                        }
                    }
                }
                else {
                    this.framefeature.resize(mapInfos[0].coords, this._firstcoords[0]);
                }
            }
        }
    }
    /**
     * create a new FrameFeature, panoCamID not mandatory
     * @param firstcoords
     * @param sensorid
     * @param distance
     * @returns {FrameFeature}
     * @private
     */
    _startFeature(firstcoords, sensorid, distance) {
        if (!isDefined(distance))
            distance = 0;
        if (!isDefined(sensorid))
            sensorid = 0;
        const mi = this.scene.getCurrentGeoframe().getMeasureInfo();
        const pos = parseInt(mi.sensors[sensorid].pos);
        let projection = "perspective";
        if (mi.streamtype == STREAMTYPE.CALOTTE)
            projection = "panoramic";
        if (mi.streamtype == STREAMTYPE.CUBEMAP)
            projection = "panoramic";
        const obj = {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: this.scene.getCurrentGeoframe().getPosition(),
            },
            properties: {
                category: "person",
                framenumber: this.scene.getCurrentGeoframe().getFramenumber(),
                segmentid: this.scene.getCurrentGeoframe().segment,
                objectid: null,
                h: 0.0,
                nx: firstcoords.x,
                ny: firstcoords.y,
                w: 0.0,
                pos: pos,
                streamid: this.scene.getCurrentGeoframe().stream,
                sensorname: null,
                distance: distance,
                projection: projection,
            },
        };
        this.framefeature = this.framefeaturecollection.createFrameFeature();
        this.framefeature.parseFromJSON(obj);
        return this.framefeature;
    }
    /**
     * removes all drawing from the scene
     * @method _cleanUp
     * @private
     */
    _cleanUp() {
        this.framefeaturecollection.destroy();
        this.toolstate = 0;
        this._firstcoords = [];
        this._lastvalidcoords = [];
        this._calloteEntered = [
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
            { entered: false, x: 0.0, y: 0.0 },
        ];
        this.scene.infrascene.traversalState.lockNavigation = false;
    }
    /**
     *  resets the tool and calls the aborted event
     *  @method reset
     *  @param {?Object} evt EventObject if called from userinput
     *  @public
     */
    reset(evt) {
        this._cleanUp();
        this.fire(FrameFeatureTool.reset, this.options["cbobject"], evt);
    }
    /**
     * returns all selected features
     * @returns {Array}
     */
    getSelectedFeatures() {
        return this._selectedFeatures;
    }
    selectFrameFeature(framefeature) {
        if (this._selectedFeatures.indexOf(framefeature) != -1) {
            const index = this._selectedFeatures.indexOf(framefeature);
            framefeature.highlightOff();
            this._selectedFeatures[index] = null;
            this._selectedFeatures.splice(index, 1);
        }
        else {
            framefeature.highlightOn();
            this._selectedFeatures.push(framefeature);
        }
        this.fire(FrameFeatureTool.selected, this.options["cbobject"], framefeature);
    }
    setStyle(style) {
        this.framefeaturecollection.changeStyle(style);
    }
}
FrameFeatureTool.enabled = "enabled";
FrameFeatureTool.disabled = "disabled";
FrameFeatureTool.deleted = "deleted";
FrameFeatureTool.reset = "reset";
FrameFeatureTool.finished = "finished";
FrameFeatureTool.selected = "selected";
//# sourceMappingURL=framefeaturetool.js.map